import React, { useState, useEffect, useRef } from "react"
import { ID } from "../../model/types"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import useOutsideClick from "../hooks/useOutsideClick"
import classNames from "classnames"

export type FuzzyOptions<Item> = {
  chooseItem: (item: Item) => void
  getIdForItem: (item: Item) => ID
  fetchItems: (filter: string) => Item[]
  renderItem: (item: Item) => React.ReactNode
  close: () => void
  placeholder: string
  title?: string
}

const FuzzyFinder = <Item extends {}>(options: FuzzyOptions<Item>) => {
  const fetchItems = options.fetchItems
  const renderItem = options.renderItem

  const [searchQuery, setSearchQuery] = useState<string>("")
  const [items, setItems] = useState<Item[]>(fetchItems(searchQuery))
  const [selectedIndex, setSelectedIndex] = useState<number>(0)

  const searchRef = useRef<HTMLInputElement>(null)
  const selectedItemRef = useRef<HTMLButtonElement | null>(null)
  const modalContainerRef = useOutsideClick(() => {
    close()
    return true
  })

  useEffect(() => {
    const n = setTimeout(() => {
      searchRef.current?.focus()
    }, 100)
    return () => clearTimeout(n)
  })

  // Keep keyboard selection in view when arrowing past the fold
  useEffect(() => {
    selectedItemRef.current?.scrollIntoView({
      block: "nearest",
      inline: "nearest",
    })
  }, [selectedIndex])

  const filterChange = (newFilter: string) => {
    setSearchQuery(newFilter)
    setItems(fetchItems(newFilter))
    setSelectedIndex(0)
  }

  const close = () => {
    searchRef.current?.blur()
    options.close()
  }

  const choose = (item: Item) => {
    options.chooseItem(item)
    close()
  }

  const highlightPrevious = (): void => {
    if (selectedIndex > 0) setSelectedIndex(selectedIndex - 1)
  }

  const highlightNext = (): void => {
    if (selectedIndex < items.length - 1) setSelectedIndex(selectedIndex + 1)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowUp":
        e.preventDefault()
        highlightPrevious()
        break
      case "ArrowDown":
        e.preventDefault()
        highlightNext()
        break
      case "Enter":
        e.preventDefault()
        if (items[selectedIndex]) choose(items[selectedIndex])
        break
      case "Escape":
        e.preventDefault()
        close()
        break
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-stone-900/25 px-1.5 pt-[min(12vh,5rem)] backdrop-blur-[2px] dark:bg-black/50 sm:px-4 sm:pt-[min(18vh,9rem)]">
      <div
        ref={modalContainerRef}
        role="dialog"
        aria-modal="true"
        aria-label={options.title || options.placeholder}
        className={classNames(
          "flex w-full max-w-2xl flex-col overflow-hidden rounded-lg sm:w-3/4",
          "border border-stone-200/80 bg-[#faf9f6]",
          "shadow-[0_1px_2px_rgba(15,23,42,0.06),0_24px_64px_-16px_rgba(15,23,42,0.35)]",
          "ring-1 ring-stone-900/[0.04]",
          "dark:border-white/10 dark:bg-gray-900",
          "dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_28px_64px_-12px_rgba(0,0,0,0.65)] dark:ring-white/10",
        )}
      >
        {options.title && (
          <div className="border-b border-stone-200/70 px-4 py-2.5 text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-400 dark:border-white/10 dark:text-stone-500">
            {options.title}
          </div>
        )}
        <div className="flex items-center gap-2.5 border-b border-stone-200/70 px-3.5 dark:border-white/10">
          <MagnifyingGlassIcon
            className="h-5 w-5 shrink-0 text-stone-400 dark:text-stone-500"
            strokeWidth={1.75}
          />
          <input
            ref={searchRef}
            value={searchQuery}
            onChange={(e) => filterChange(e.target.value)}
            onKeyDown={(e) => handleKeyPress(e)}
            type="text"
            className="h-12 w-full border-0 bg-transparent px-0 text-[15px] text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-0 dark:text-stone-100 dark:placeholder:text-stone-500"
            placeholder={options.placeholder}
            data-testid="fuzzy-finder-input"
          />
        </div>
        {items.length > 0 ? (
          <ul className="max-h-[min(60vh,32rem)] overflow-y-auto py-1.5">
            {items.map((item: Item, index: number) => (
              <li key={`idea-${options.getIdForItem(item)}`}>
                <button
                  type="button"
                  ref={selectedIndex === index ? selectedItemRef : null}
                  onClick={() => choose(item)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={classNames(
                    "flex w-full flex-row items-center gap-3 px-3.5 py-2.5 text-left transition-colors focus:outline-none",
                    {
                      "bg-stone-900/[0.06] dark:bg-white/[0.08]":
                        selectedIndex === index,
                      "hover:bg-stone-900/[0.03] dark:hover:bg-white/[0.04]":
                        selectedIndex !== index,
                    },
                  )}
                >
                  {renderItem(item)}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="px-4 py-6 text-center text-sm text-stone-400 dark:text-stone-500">
            {searchQuery ? "No matches" : "Type to search"}
          </div>
        )}
      </div>
    </div>
  )
}

export default FuzzyFinder
